/**
 * getRerunDefaults — single source of truth for rerun-form prefills.
 *
 * Three of the report's most consequential generation inputs were
 * dropped on every rerun until 2026-05-09 because the per-type panels
 * read only from `report.rerunConfig` and that struct didn't carry
 * them: `typedPreferences` (per-type wizard answers), `documentRefs`
 * (user-curated doc selection), and `companyArchetypeOverride`
 * (reader profile that framed the prompt).
 *
 * From the next deploy on, generateTypedReport stamps these onto
 * `rerunConfig` at write time. But every report stored before that
 * fix only has the values at the top level. This helper hides the
 * precedence so panels can call it once and trust the result:
 *
 *     rerunConfig.X  →  report.X  →  undefined
 *
 * Callers should forward the returned object to generateTypedReport so
 * a rerun reproduces the parent's framing instead of resetting to
 * platform defaults.
 */
import type { SiteRagReport } from '../types/siteRag';

export interface RerunDefaults {
  typedPreferences?: SiteRagReport['typedPreferences'];
  documentRefs?: SiteRagReport['documentRefs'];
  archetypeOverride?: SiteRagReport['companyArchetypeOverride'];
  /**
   * UD5 — per-checklist-row evidence-tier choice from the prior
   * report. tier1-doc / tier2-reuse / tier3-baseline / tier4-synthesis
   * / pending. Threaded into the rerun so the user doesn't re-pick
   * "Regional baseline (50%)" for every row.
   */
  evidenceStrategy?: SiteRagReport['evidenceStrategy'];
}

/**
 * Request-shape guard (2026-08-13, Redhead audit-tab regenerate RCA): the
 * generation request schema accepts an ABSENT typedPreferences map (the
 * server derives that type's defaults) but rejects a PRESENT map missing the
 * selected type's own key ("typedPreferences must include the selected
 * report type"). Legacy reports written before typedPreferences persistence
 * carry maps keyed only by OTHER types — forwarding them verbatim 400'd
 * every audit-tab regenerate for public-sentiment and water-resource.
 *
 * Cross-type entries are MEANINGFUL to a run (Codex review on #132/#133:
 * every generation reads `typedPreferences.environmental` during SED prep
 * and `['power-availability']` when binding the power-strategy snapshot),
 * so a keyless map must NOT be dropped wholesale. Instead the selected
 * type's slot is filled with `{}` — every per-type schema is all-optional,
 * so an empty object passes validation and the server derives that type's
 * defaults exactly as an absent map would, while the other types' framing
 * survives. An EMPTY map still normalizes to absent (nothing to preserve).
 */
export function normalizeTypedPreferencesForRequest(
  typedPreferences: SiteRagReport['typedPreferences'],
  reportTypeId: string,
): SiteRagReport['typedPreferences'] {
  if (!typedPreferences) return undefined;
  const map = typedPreferences as Record<string, unknown>;
  if (map[reportTypeId] !== undefined) return typedPreferences;
  if (Object.keys(map).length === 0) return undefined;
  return { ...typedPreferences, [reportTypeId]: {} } as SiteRagReport['typedPreferences'];
}

export function getRerunDefaults(report: SiteRagReport): RerunDefaults {
  const cfg = report.rerunConfig;

  // Each field gets resolved independently — a partially-stamped
  // rerunConfig (say it has typedPreferences but not documentRefs) still
  // falls back to the top-level for the missing slots.
  const typedPreferences = cfg?.typedPreferences ?? report.typedPreferences;
  const documentRefs = cfg?.documentRefs ?? report.documentRefs;
  const archetypeOverride = cfg?.companyArchetypeOverride ?? report.companyArchetypeOverride;
  // UD5 — `evidenceStrategy` lives directly on the report (siteRag.ts:905);
  // not yet on rerunConfig, so always read from the top level.
  const evidenceStrategy = report.evidenceStrategy;

  return { typedPreferences, documentRefs, archetypeOverride, evidenceStrategy };
}
